import React, { useState } from "react";
import {
  AnimatedSizeContainer,
  Button,
  InfoTooltip,
  LinkPreview,
  SimpleTooltipContent,
  Tooltip,
  useMediaQuery,
} from "@panda0/panda0-ui";
import { AnimatePresence, motion } from "framer-motion";
import { Copy, Download, LinkIcon, Mail, QrCodeIcon } from "lucide-react";

import { QRCode } from "@/ui/shared/qr-code";

// Link Sharing Tab Component - Updated with more compact buttons
function LinkSharingTab({
  shortLink,
  jobDetails,
  copyLink,
}: {
  shortLink: string;
  jobDetails: any;
  copyLink: () => void;
}) {
  const { isMobile } = useMediaQuery();
  const [showQRCode, setShowQRCode] = useState(false);

  return (
    <div className="space-y-3">
      <div className="rounded-xl border border-neutral-200 bg-white p-6">
        <div className="space-y-4">
          <h3 className="text-lg font-medium flex items-center gap-2">
            <LinkIcon className="h-5 w-5 text-neutral-700" />
            PandaQuiz Link
            <InfoTooltip
              content={
                <SimpleTooltipContent
                  title="Share this link with candidates to allow them to take the PandaQuiz."
                  cta="Learn more about sharing options."
                  href="#"
                />
              }
            />
          </h3>

          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              {/* Link Builder Modal */}
              <LinkPreview defaultUrl={shortLink} />
            </div>
          </div>

          {/* Compact sharing buttons */}
          <div className="flex flex-wrap gap- mt-4">
            <div className="flex flex-wrap gap-1">
              <Tooltip content="Show QR Code">
                <Button
                  variant="secondary"
                  onClick={() => setShowQRCode(!showQRCode)}
                  className="h-9 w-9 p-0 flex items-center justify-center"
                  icon={<QrCodeIcon className="size-4 text-neutral-700" />}
                />
              </Tooltip>
              <Tooltip content="Copy Link">
                <Button
                  variant="secondary"
                  onClick={copyLink}
                  className="h-9 w-9 p-0 flex items-center justify-center"
                  icon={<Copy className="h-4 w-4" />}
                />
              </Tooltip>
              <Tooltip content="Email Link">
                <Button
                  variant="secondary"
                  onClick={() =>
                    window.open(
                      `mailto:?subject=${encodeURIComponent(`${jobDetails.title} Technical Quiz`)}&body=${encodeURIComponent(`Please complete the technical quiz at: ${shortLink}`)}`,
                      "_blank"
                    )
                  }
                  className="h-9 w-9 p-0 flex items-center justify-center"
                  icon={<Mail className="h-4 w-4" />}
                />
              </Tooltip>

              {/* Social sharing */}
              <Tooltip content="Share on LinkedIn">
                <Button
                  variant="secondary"
                  onClick={() =>
                    window.open(
                      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shortLink)}`,
                      "_blank"
                    )
                  }
                  className="h-9 w-9 p-0 flex items-center justify-center"
                  icon={
                    <svg
                      className="h-4 w-4"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.32 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.79M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z" />
                    </svg>
                  }
                />
              </Tooltip>
              <Tooltip content="Share on Twitter">
                <Button
                  variant="secondary"
                  onClick={() =>
                    window.open(
                      `https://twitter.com/intent/tweet?text=${encodeURIComponent(`${jobDetails.title} Quiz - ${shortLink}`)}`,
                      "_blank"
                    )
                  }
                  className="h-9 w-9 p-0 flex items-center justify-center"
                  icon={
                    <svg
                      className="h-4 w-4"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M22.46 6c-.77.35-1.6.58-2.46.69.88-.53 1.56-1.37 1.88-2.38-.83.5-1.75.85-2.72 1.05C18.37 4.5 17.26 4 16 4c-2.35 0-4.27 1.92-4.27 4.29 0 .34.04.67.11.98C8.28 9.09 5.11 7.38 3 4.79c-.37.63-.58 1.37-.58 2.15 0 1.49.75 2.81 1.91 3.56-.71 0-1.37-.2-1.95-.5v.03c0 2.08 1.48 3.82 3.44 4.21a4.22 4.22 0 0 1-1.93.07 4.28 4.28 0 0 0 4 2.98 8.521 8.521 0 0 1-5.33 1.84c-.34 0-.68-.02-1.02-.06C3.44 20.29 5.7 21 8.12 21 16 21 20.33 14.46 20.33 8.79c0-.19 0-.37-.01-.56.84-.6 1.56-1.36 2.14-2.23z" />
                    </svg>
                  }
                />
              </Tooltip>
              <Tooltip content="Share on Facebook">
                <Button
                  variant="secondary"
                  onClick={() =>
                    window.open(
                      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shortLink)}`,
                      "_blank"
                    )
                  }
                  className="h-9 w-9 p-0 flex items-center justify-center"
                  icon={
                    <svg
                      className="h-4 w-4"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M12 2.04c-5.5 0-10 4.49-10 10.02 0 5 3.66 9.15 8.44 9.9v-7H7.9v-2.9h2.54V9.85c0-2.51 1.49-3.89 3.78-3.89 1.09 0 2.23.19 2.23.19v2.47h-1.26c-1.24 0-1.63.77-1.63 1.56v1.88h2.78l-.45 2.9h-2.33v7a10 10 0 0 0 8.44-9.9c0-5.53-4.5-10.02-10-10.02z" />
                    </svg>
                  }
                />
              </Tooltip>

              <Tooltip content="Download QR Code">
                <Button
                  variant="secondary"
                  className="h-9 w-9 p-0 flex items-center justify-center"
                  icon={<Download className="h-4 w-4" />}
                />
              </Tooltip>
            </div>
          </div>

          <AnimatedSizeContainer height>
            {showQRCode && (
              <div className="flex flex-col sm:flex-row items-center gap-6 mt-6 p-6 border border-neutral-200 rounded-xl bg-neutral-50">
                <div className="h-56 w-56 flex-shrink-0 bg-white p-4 rounded-xl shadow-sm flex items-center justify-center">
                  <div className="h-48 w-48 flex items-center justify-center">
                    <AnimatePresence mode="wait">
                      <motion.div
                        key={shortLink}
                        initial={{ filter: "blur(2px)", opacity: 0.4 }}
                        animate={{ filter: "blur(0px)", opacity: 1 }}
                        exit={{ filter: "blur(2px)", opacity: 0.4 }}
                        transition={{ duration: 0.1 }}
                        className="relative flex size-full items-center justify-center"
                      >
                        <QRCode url={shortLink} scale={2.0} />
                      </motion.div>
                    </AnimatePresence>
                  </div>
                </div>
                <div className="space-y-4 flex-1">
                  <h4 className="text-base font-medium">
                    QR Code for PandaQuiz
                  </h4>
                  <p className="text-sm text-neutral-600">
                    Use this QR code in printed materials or displays to provide
                    quick access to the PandaQuiz. Candidates can scan it with
                    their phone's camera to open the link directly.
                  </p>
                </div>
              </div>
            )}
          </AnimatedSizeContainer>
        </div>
      </div>
    </div>
  );
}

export default React.memo(LinkSharingTab);
